import { isDefined } from "@clipboard-health/util-ts";
import type { ClipboardScoreHistoryResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";

const WORK_SHIFT_ACTIONS = new Set(["WORK_SHIFT", "CLOCKIN_TIMELINESS_BONUS", "START_SHIFT_LATE"]);

const ACTION_REASON_MAP: Record<string, string> = {
  WORK_SHIFT: "finishing your shift",
  CLOCKIN_TIMELINESS_BONUS: "arriving on time",
  START_SHIFT_LATE: "arriving late",
};

export interface ClipboardScoreChangeResult {
  /** Total score change (positive for increase, negative for decrease) */
  scoreChange: number;
  /** Current score after all changes */
  currentScore: number;
  /** Score before this shift */
  previousScore: number;
  /** Array of human-readable reasons for the score changes */
  reasons: string[];
}

/**
 * Calculates attendance score changes from clipboard score logs for a specific shift
 */
export function calculateClipboardScoreChange(
  clipboardScoreLogs: ClipboardScoreHistoryResponse | undefined
): ClipboardScoreChangeResult | undefined {
  if (!clipboardScoreLogs?.scoreLogs) {
    return undefined;
  }

  // Filter logs for this shift and work-related actions
  const workShiftScoreLogs = clipboardScoreLogs.scoreLogs
    .filter((scoreLog) => WORK_SHIFT_ACTIONS.has(scoreLog.action))
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  if (workShiftScoreLogs.length === 0) {
    return undefined;
  }

  // eslint-disable-next-line prefer-destructuring
  const firstScoreLog = workShiftScoreLogs[0];
  const lastScoreLog = workShiftScoreLogs.at(-1);

  if (
    !isDefined(firstScoreLog?.clipboardScoreUpdatedScore) ||
    !isDefined(firstScoreLog?.clipboardScoreAdjustment) ||
    !isDefined(lastScoreLog?.clipboardScoreUpdatedScore)
  ) {
    return undefined;
  }

  // Current score is the updated score from the last log
  const currentScore = lastScoreLog.clipboardScoreUpdatedScore;

  // Score change is the sum of all changes related to this shift
  const scoreChange = workShiftScoreLogs.reduce(
    (accumulator, { clipboardScoreAdjustment }) => accumulator + (clipboardScoreAdjustment ?? 0),
    0
  );

  // Calculate previous score (before first change)
  const previousScore = currentScore - scoreChange;

  // Extract reasons for changes
  const reasons: string[] = [];

  for (const log of workShiftScoreLogs) {
    const { clipboardScoreAdjustment: adjustment, action } = log;

    // Only include actions that actually changed the score
    if (adjustment !== 0 && ACTION_REASON_MAP[action]) {
      reasons.push(ACTION_REASON_MAP[action]);
    }
  }

  return {
    scoreChange,
    currentScore,
    previousScore,
    reasons,
  };
}
